import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IUserProfileModel, IAuthResponse } from '../../shared/models/userProfile.model';
import { AlertModel } from '../../shared/models/alert.model';



export interface Em3Permission {
    id: string;
    name: string;
    description: string;
}

export interface UserWithRole {
    id: string;
    sub: string;
    email: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    createdOn: string;
    roleId: string;
    roleName: string;
    /** Keycloak federated-identity alias (e.g. "azure", "cufi-ad"). Null for native Keycloak users. */
    identityProvider?: string | null;
}

export interface RoleWithPermissions {
    id: string;
    name: string;
    description: string;
    parentRoleId?: string | null;
    permissions: RolePermissionItem[];
}

export interface RolePermissionItem {
    id: string;
    name: string;
    /** True when this permission is inherited from an ancestor role rather than granted directly. */
    inherited?: boolean;
}

@Injectable() export class UserService {
    constructor(private http: HttpClient) { }


    getUserInfo(): Observable<IUserProfileModel> {
        return this.http.get<IUserProfileModel>(`${environment.api.baseUrl}/users/me`);
    }


    login(username: string, password: string): Observable<IAuthResponse> {
        return this.http.post<IAuthResponse>(`${environment.api.baseUrl}/login`, { username, password });
    }


    sendAlert(eventId: string, payload: AlertModel) {
        return this.http.post<IAuthResponse>(`${environment.api.baseUrl}/users/send-alert`, { ...payload, eventId: eventId });
    }


    getAdminUsers(): Observable<UserWithRole[]> {
        return this.http.get<UserWithRole[]>(`${environment.api.baseUrl}/admin/users`);
    }


    getAdminUser(userId: string): Observable<UserWithRole> {
        return this.http.get<UserWithRole>(`${environment.api.baseUrl}/admin/users/${userId}`);
    }


    updateUser(userId: string, body: { roleId: string; firstName: string; lastName: string; isActive: boolean }): Observable<void> {
        return this.http.put<void>(`${environment.api.baseUrl}/admin/users/${userId}`, body);
    }


    getAdminRoles(): Observable<RoleWithPermissions[]> {
        return this.http.get<RoleWithPermissions[]>(`${environment.api.baseUrl}/admin/roles`);
    }


    getAdminRole(roleId: string): Observable<RoleWithPermissions> {
        return this.http.get<RoleWithPermissions>(`${environment.api.baseUrl}/admin/roles/${roleId}`);
    }


    getAdminPermissions(): Observable<Em3Permission[]> {
        return this.http.get<Em3Permission[]>(`${environment.api.baseUrl}/admin/permissions`);
    }


    createRole(body: { name: string; description: string; parentRoleId?: string | null }): Observable<void> {
        return this.http.post<void>(`${environment.api.baseUrl}/admin/roles`, body);
    }


    setRoleParent(roleId: string, parentRoleId: string | null): Observable<void> {
        return this.http.put<void>(`${environment.api.baseUrl}/admin/roles/${roleId}/parent`, { parentRoleId });
    }


    updateRolePermissions(roleId: string, permissionIds: string[]): Observable<void> {
        return this.http.put<void>(`${environment.api.baseUrl}/admin/roles/${roleId}/permissions`, { permissionIds });
    }


    deleteRole(roleId: string): Observable<void> {
        return this.http.delete<void>(`${environment.api.baseUrl}/admin/roles/${roleId}`);
    }


    createKeycloakUser(body: CreateKeycloakUserBody): Observable<CreateKeycloakUserResult> {
        return this.http.post<CreateKeycloakUserResult>(`${environment.api.baseUrl}/admin/keycloak-users`, body);
    }


    resetKeycloakPassword(userId: string, body: ResetPasswordBody): Observable<void> {
        return this.http.post<void>(`${environment.api.baseUrl}/admin/keycloak-users/${userId}/reset-password`, body);
    }


    deactivateUser(userId: string): Observable<void> {
        return this.http.post<void>(`${environment.api.baseUrl}/admin/keycloak-users/${userId}/deactivate`, {});
    }


    reactivateUser(userId: string): Observable<void> {
        return this.http.post<void>(`${environment.api.baseUrl}/admin/keycloak-users/${userId}/reactivate`, {});
    }


    deleteUser(userId: string): Observable<void> {
        return this.http.delete<void>(`${environment.api.baseUrl}/admin/keycloak-users/${userId}`);
    }
}

export interface CreateKeycloakUserBody {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    temporaryPassword: boolean;
    roleId?: string;
}

export interface CreateKeycloakUserResult {
    sub: string;
    userId: string;
}

export interface ResetPasswordBody {
    newPassword: string;
    temporary: boolean;
}
